import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { FormulationSection } from '../../components/training/FormulationEditor';
import trainingApi from '../../services/trainingApi';

const BACKGROUND_HINTS = {
  psychiatric: 'Previous psychiatric diagnoses, treatments, hospitalisations, self-harm history.',
  medical: 'Relevant medical conditions, medications, allergies.',
  family: 'Family psychiatric history, family structure, relationships.',
  social: 'Living situation, employment, social supports, education.',
  substance: 'Alcohol, drugs, tobacco use. Quantity, frequency, consequences.',
};

const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

const FormulationPage = () => {
  const { id: sessionId } = useParams();
  const navigate = useNavigate();

  const [formulation, setFormulation] = useState({
    presentingComplaint: '',
    historyOfPresentingComplaint: '',
    backgroundHistory: {
      psychiatric: '',
      medical: '',
      family: '',
      social: '',
      substance: '',
    },
    mentalStateExamination: '',
    riskAssessment: '',
    differentialDiagnosis: [{ id: '1', diagnosis: '', reasoning: '', rank: 1 }],
    managementPlan: '',
  });

  const [session, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [maxWords] = useState(2000);

  // Load session data and any saved draft
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const response = await trainingApi.getSession(sessionId);
        const body = response.data || response;
        const data = body.data || body;
        setSession(data.session || data);

        // Load draft from localStorage
        const savedDraft = localStorage.getItem(`formulation_draft_${sessionId}`);
        if (savedDraft) {
          try {
            const parsed = JSON.parse(savedDraft);
            setFormulation(parsed);
          } catch (e) {
            // Ignore corrupt draft
          }
        }
      } catch (err) {
        setError('Failed to load session data');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [sessionId]);

  // Auto-save to localStorage with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      localStorage.setItem(`formulation_draft_${sessionId}`, JSON.stringify(formulation));
      setSaveStatus('saved');
    }, 5000);

    setSaveStatus('unsaved');
    return () => clearTimeout(timer);
  }, [formulation, sessionId]);

  const updateField = useCallback((field, value) => {
    setFormulation(prev => ({ ...prev, [field]: value }));
  }, []);

  const updateBackgroundField = useCallback((subfield, value) => {
    setFormulation(prev => ({
      ...prev,
      backgroundHistory: { ...prev.backgroundHistory, [subfield]: value },
    }));
  }, []);

  // Differential diagnosis management
  const addDiagnosis = useCallback(() => {
    setFormulation(prev => ({
      ...prev,
      differentialDiagnosis: [
        ...prev.differentialDiagnosis,
        { id: String(Date.now()), diagnosis: '', reasoning: '', rank: prev.differentialDiagnosis.length + 1 },
      ],
    }));
  }, []);

  const removeDiagnosis = useCallback((diagId) => {
    setFormulation(prev => ({
      ...prev,
      differentialDiagnosis: prev.differentialDiagnosis.filter(d => d.id !== diagId),
    }));
  }, []);

  const updateDiagnosis = useCallback((diagId, field, value) => {
    setFormulation(prev => ({
      ...prev,
      differentialDiagnosis: prev.differentialDiagnosis.map(d =>
        d.id === diagId ? { ...d, [field]: value } : d
      ),
    }));
  }, []);

  // Word count
  const computeWordCount = () => {
    const allText = [
      formulation.presentingComplaint,
      formulation.historyOfPresentingComplaint,
      ...Object.values(formulation.backgroundHistory),
      formulation.mentalStateExamination,
      formulation.riskAssessment,
      formulation.managementPlan,
      ...formulation.differentialDiagnosis.map(d => `${d.diagnosis} ${d.reasoning}`),
    ].join(' ');

    return allText.trim().split(/\s+/).filter(Boolean).length;
  };

  const wordCount = computeWordCount();

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await trainingApi.submitFormulation(sessionId, formulation);
      localStorage.removeItem(`formulation_draft_${sessionId}`);
      navigate(`/training/session/${sessionId}/reflection`);
    } catch (err) {
      setError(err.message || 'Failed to submit formulation');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', py: 3 }}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" gutterBottom>Clinical Formulation</Typography>
          <Typography variant="body2" color="text.secondary">
            {session?.scenarioName || 'Training Session'}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography
            variant="body2"
            color={wordCount > maxWords ? 'error.main' : 'text.secondary'}
          >
            {wordCount} / {maxWords} words
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Draft saved' : 'Unsaved changes'}
          </Typography>
        </Box>
      </Box>

      {/* Presenting Complaint */}
      <FormulationSection
        title="Presenting Complaint"
        hint="Summarise the patient's main reason for presentation. Use the patient's own language where appropriate."
        value={formulation.presentingComplaint}
        onChange={(val) => updateField('presentingComplaint', val)}
        maxWordGuide={100}
      />

      {/* History of Presenting Complaint */}
      <FormulationSection
        title="History of Presenting Complaint"
        hint="Chronological account: onset, duration, severity, precipitating factors, associated symptoms, impact on functioning."
        value={formulation.historyOfPresentingComplaint}
        onChange={(val) => updateField('historyOfPresentingComplaint', val)}
        maxWordGuide={300}
      />

      {/* Background History */}
      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Background History</Typography>
      {['psychiatric', 'medical', 'family', 'social', 'substance'].map(subfield => (
        <FormulationSection
          key={subfield}
          title={capitalize(subfield)}
          hint={BACKGROUND_HINTS[subfield]}
          value={formulation.backgroundHistory[subfield]}
          onChange={(val) => updateBackgroundField(subfield, val)}
          maxWordGuide={100}
          compact
        />
      ))}

      {/* Mental State Examination */}
      <FormulationSection
        title="Mental State Examination"
        hint="Systematic observations: appearance, behaviour, speech, mood (subjective & objective), affect, thought form, thought content, perception, cognition, insight, judgement."
        value={formulation.mentalStateExamination}
        onChange={(val) => updateField('mentalStateExamination', val)}
        maxWordGuide={250}
      />

      {/* Risk Assessment */}
      <FormulationSection
        title="Risk Assessment"
        hint="Structured assessment of risk to self, risk to others, risk from others. Include specific risk factors, protective factors, and dynamic risk formulation."
        value={formulation.riskAssessment}
        onChange={(val) => updateField('riskAssessment', val)}
        maxWordGuide={200}
      />

      {/* Differential Diagnosis */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Differential Diagnosis (ranked by likelihood)
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          List your diagnoses in order of likelihood. For each, provide the ICD-10/DSM-5 code and your clinical reasoning.
        </Typography>

        {formulation.differentialDiagnosis.map((entry, index) => (
          <Box key={entry.id} sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'flex-start' }}>
            <Typography variant="body2" sx={{ mt: 1, minWidth: 24, fontWeight: 600 }}>
              {index + 1}.
            </Typography>
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                size="small"
                label="Diagnosis (with code)"
                value={entry.diagnosis}
                onChange={(e) => updateDiagnosis(entry.id, 'diagnosis', e.target.value)}
                sx={{ mb: 0.5 }}
              />
              <TextField
                fullWidth
                size="small"
                label="Reasoning"
                multiline
                maxRows={3}
                value={entry.reasoning}
                onChange={(e) => updateDiagnosis(entry.id, 'reasoning', e.target.value)}
              />
            </Box>
            <IconButton
              size="small"
              onClick={() => removeDiagnosis(entry.id)}
              disabled={formulation.differentialDiagnosis.length <= 1}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}

        <Button size="small" startIcon={<AddIcon />} onClick={addDiagnosis}>
          Add Differential
        </Button>
      </Paper>

      {/* Management Plan */}
      <FormulationSection
        title="Management Plan"
        hint="Biopsychosocial approach: immediate actions, short-term plan, longer-term considerations. Include the patient in shared decision-making where appropriate."
        value={formulation.managementPlan}
        onChange={(val) => updateField('managementPlan', val)}
        maxWordGuide={200}
      />

      {/* Actions */}
      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={() => {
            localStorage.setItem(`formulation_draft_${sessionId}`, JSON.stringify(formulation));
            setSaveStatus('saved');
          }}
        >
          Save Draft
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || wordCount === 0}
        >
          {isSubmitting ? <CircularProgress size={20} /> : 'Submit Formulation'}
        </Button>
      </Box>
    </Box>
  );
};

export default FormulationPage;
